import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  useGetPowerBiReports,
  useGetReportSearchCategory,
  useGetReportSearchType,
} from "../../api/services/reports/reportsApi";
import { getValueOrBlank } from "../../helpers/utils";
import { useSiteCore } from "../../hooks/useSiteCore";
import { siteCoreContentLoadingSelector } from "../../redux/slices/appState/appStateSelector";
import {
  toggleLoader,
  toggleToast,
} from "../../redux/slices/appState/appStateSlice";
import { SearchReportStyleWrapper } from "./ReportSearchStyle";
import { CoachReportSearch } from "./components/coachReport/coachReportSearch/CoachReportSearch";
import { NotificationStatusReportSearch } from "./components/notificationStatusReport/notificationStatusReportSearch/NotificationStatusReportSearch";
import { OutreachReportSearch } from "./components/outreachReport/outreachReportSearch/OutreachReportSearch";
import { PendingBillingSearch } from "./components/pendingBillingSearch/PendingBillingSearch";
import { PowerBIReports } from "./components/powerBIReports/PowerBIReports";
import { ReportChangeModal } from "./components/reportChangeModal/ReportChangeModal";
import { ReportsSearchForm } from "./components/reportSearchForm/ReportSearchForm";
import { SearchReportHeader } from "./components/reportSearchHeader/ReportSearchHeader";
import { getReportSearchFormSchema } from "./modelsAndSchemas/ValidationSchema";
import {
  PendingBillingSearchFormValues,
  PowerBiReportValues,
  selectPendingBillingFormKeys,
} from "./modelsAndSchemas/models";
import {
  powerBIReportSiteCoreKeys,
  reportsSearchReportsSiteCoreKeys,
  selectReportSiteCoreKeys,
} from "./siteCore/SiteCoreKeys";
import { NCBPReportSearch } from "./components/ncbpReport/ncbpReportSearch/NCBPReportSearch";

export const SearchReport = () => {
  const dispatch = useDispatch();
  const {
    commonLabels,
    commonValidationMessages: { requiredValidation },
    fetchLabelValueFromLOVListWithImage,
    fetchValueByKeyOfDropdown,
  } = useSiteCore();
  const methods = useForm<PendingBillingSearchFormValues>({
    defaultValues: {
      categoryId: "",
      typeId: "",
    },
    resolver: yupResolver(getReportSearchFormSchema(requiredValidation)),
  });

  const { handleSubmit, getValues, reset, setValue } = methods;

  const [listOfReports, setListOfReports] = useState<PowerBiReportValues[]>([]);
  const [isParamSet, setIsParamSet] = useState<boolean>(false);

  const [state, setState] = useState<{ showChangeReportModal: boolean }>({
    showChangeReportModal: false,
  });

  const [modalFormStateValues, setModalFormStateValues] =
    useState<PendingBillingSearchFormValues>({
      categoryId: "",
      typeId: "",
    } as PendingBillingSearchFormValues);
  const [selectedReportId, setSelectedReportId] = useState<string>("");

  const setFormValueToState = () => {
    const { categoryId, typeId } = getValues();
    setModalFormStateValues({ categoryId, typeId });
  };

  const handleChangeReport = () => {
    setState((prevState) => ({
      ...prevState,
      showChangeReportModal: true,
    }));
    setFormValueToState();
    const { categoryId } = getValues();
    getReportTypes({ categoryId });
  };

  const {
    isPending: isCategoryLoading,
    isError: isCategoryError,
    data: reportSearchCategory,
    mutateAsync: getReportSearchCategory,
  } = useGetReportSearchCategory();

  const {
    isPending: isReportTypesLoading,
    isError: isReportTypesError,
    data: reportTypes,
    mutateAsync: getReportTypes,
  } = useGetReportSearchType();

  const {
    isPending: isPowerBiReportLoading,
    isError: isPowerBiReportError,
    data: reportList,
  } = useGetPowerBiReports(true);

  const isLoading =
    isCategoryLoading || isReportTypesLoading || isPowerBiReportLoading;
  const isApiError =
    isCategoryError || isReportTypesError || isPowerBiReportError;

  const isSiteCoreLoading = useSelector(siteCoreContentLoadingSelector);

  useEffect(() => {
    if (isLoading) {
      dispatch(
        toggleLoader({
          isVisible: true,
        }),
      );
    } else {
      dispatch(
        toggleLoader({
          isVisible: false,
        }),
      );
    }
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isLoading]);

  useEffect(() => {
    if (isApiError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
    }
  }, [isApiError]);

  const handleSearchFormSubmit = () => {
    setFormValueToState();
    const { typeId } = getValues();
    setSelectedReportId(typeId);

    if (state.showChangeReportModal) {
      setState((prevState) => ({
        ...prevState,
        showChangeReportModal: false,
      }));
    }

    setIsParamSet(true);
  };

  useEffect(() => {
    getReportSearchCategory();
  }, []);

  const { typeId } = useWatch({
    control: methods.control,
  });

  const handleCategoryChange = (categoryId: string) => {
    getReportTypes({ categoryId });
    setValue(selectPendingBillingFormKeys.typeId, "");
  };

  const reportTypesData = useMemo(() => {
    const reports = reportTypes?.data ?? [];
    return reports?.map((type) => {
      return {
        ...type,
        sitecoreKey: type.name,
        name: fetchValueByKeyOfDropdown(
          reportsSearchReportsSiteCoreKeys.header,
          selectReportSiteCoreKeys.reportTypeList,
          getValueOrBlank(type.name ?? ""),
        ),
      };
    });
  }, [reportTypes, isSiteCoreLoading]);

  const subTitle = useMemo(() => {
    if (!isParamSet && !modalFormStateValues.typeId) {
      return "";
    }
    const type = reportTypesData?.find(({ id }) => id === typeId)?.name;
    return type;
  }, [isParamSet, modalFormStateValues.typeId]);

  const handleCloseChangeModal = () => {
    setState((prevState) => ({
      ...prevState,
      showChangeReportModal: false,
    }));
    reset({
      categoryId: modalFormStateValues.categoryId,
      typeId: modalFormStateValues.typeId,
    });
  };

  const prepareSiteCoreObject = (reportId: string) => {
    const reportObj = fetchLabelValueFromLOVListWithImage(
      powerBIReportSiteCoreKeys.header,
      powerBIReportSiteCoreKeys.powerBIReportList,
      reportId,
    );

    return {
      reportName: reportObj?.Value || "",
      reportImagePath: reportObj?.ImagePath?.ImageUrl,
      reportImageAltText: reportObj?.ImagePath?.AltText,
    };
  };

  useEffect(() => {
    setListOfReports(
      reportList &&
        reportList.map((report: PowerBiReportValues) => ({
          ...report,
          ...prepareSiteCoreObject(getValueOrBlank(report.reportSiteCoreId)),
        })),
    );
  }, [reportList, isSiteCoreLoading]);

  const renderSelectedReportSearchPage = useCallback(() => {
    const {
      pendingBillingList,
      notificationStatus,
      outreachList,
      coachCaseloads,
      ncbpReport,
    } = selectReportSiteCoreKeys;
    const selectedReportType = reportTypesData?.find(
      (reportType) => selectedReportId === reportType.id,
    );

    if (!selectedReportType) {
      return null;
    } else if (selectedReportType.sitecoreKey === pendingBillingList) {
      return <PendingBillingSearch />;
    } else if (selectedReportType.sitecoreKey === notificationStatus) {
      return <NotificationStatusReportSearch />;
    } else if (selectedReportType.sitecoreKey === outreachList) {
      return <OutreachReportSearch />;
    } else if (selectedReportType.sitecoreKey === coachCaseloads) {
      return <CoachReportSearch />;
    } else if (selectedReportType.sitecoreKey === ncbpReport) {
      return <NCBPReportSearch />;
    }
  }, [selectedReportId]);

  return (
    <SearchReportStyleWrapper>
      {isParamSet ? (
        <>
          <FormProvider {...methods}>
            <form>
              <ReportChangeModal
                reportCategories={reportSearchCategory?.data ?? []}
                onSubmit={handleSubmit(handleSearchFormSubmit)}
                reportTypes={reportTypesData}
                isOpen={state.showChangeReportModal}
                onCancel={handleCloseChangeModal}
                handleCategoryChange={handleCategoryChange}
              ></ReportChangeModal>
            </form>
          </FormProvider>
          <SearchReportHeader
            subTitle={subTitle}
            subTitleActionClick={handleChangeReport}
          />

          {renderSelectedReportSearchPage()}
        </>
      ) : (
        <>
          <SearchReportHeader />
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(handleSearchFormSubmit)}>
              <ReportsSearchForm
                reportCategories={reportSearchCategory?.data ?? []}
                reportTypes={reportTypesData}
                handleCategoryChange={handleCategoryChange}
              />
            </form>
          </FormProvider>

          {listOfReports && listOfReports.length > 0 && (
            <PowerBIReports reportList={listOfReports} />
          )}
        </>
      )}
    </SearchReportStyleWrapper>
  );
};
